import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../../components/TanTable';
import { useTryPermissions } from '../../../contexts/PermissionContext';
import {
  useAdditionalFieldColumns,
  useAdditionalFieldSelection,
} from '../../../hooks/useAdditionalFields';
import { useOrderFieldExtras } from './useOrderFieldExtras';
import { ServiceFailureDetail, LateDetail, UncodedDetail, ApptFinalizedDetail, DetailKind } from './logic';

/**
 * Detail popup for a Service Failures / OTD / Uncoded / Appts Finalized cell,
 * rendered as a filterable, sortable, groupable TanTable (sticky header). One
 * row per record. The Pro # opens the Pro drawer and turns green once viewed; a
 * header link opens all pros in a search window. SF and OTD share the same
 * columns (late deliveries are controllable SFs); Uncoded and Appts Finalized
 * show only the delivery context (+ a date column).
 *
 * Any order field can be added as a column from the grid's own picker, the same
 * as on every other pro grid. The selection is remembered per drill KIND — the
 * columns that make sense beside a service failure are rarely the ones you want
 * beside an uncoded delivery — and the values are fetched separately and merged
 * on (see `useOrderFieldExtras`), because these rows are derived in the browser
 * rather than fetched.
 */

export type { DetailKind };
type Row = (ServiceFailureDetail | LateDetail | UncodedDetail | ApptFinalizedDetail) & Record<string, any>;

interface Props {
  open: boolean;
  onClose: () => void;
  kind: DetailKind;
  title: string;
  rows: Row[];
  pros: number[];               // distinct pros, for the "open in search" link
  /** The page's date range, passed through to the added-columns lookup. */
  range?: [string, string] | null;
  onProClick: (pro: number) => void;
  // Uncoded modal only: the Planned Date opens a New Service Failure record for
  // the pro, defaulted to that planned date. billto rides along so the editor can
  // decide whether to show THD Reason — it is THD-family-only, and the editor has no
  // other way to learn the bill-to from here.
  onAddServiceFailure?: (pro: number, date: string, billto?: number | null) => void;
}

/**
 * Registry fields these built-in columns already render, so the picker doesn't
 * offer them again — and so each column takes the registry's name for the field,
 * reading the same here as on every other grid.
 *
 * `date` is deliberately absent: it is the reschedule / planned / finalized date,
 * which no orders-scope def resolves.
 */
const DETAIL_COVERED_FIELDS = {
  pro: ['pronumber'],
  customer: ['customername'],
  delivery_location: ['destname'],
  delivery_appt: ['d_destapptdate'],
} as const;

// 'YYYY-MM-DD' -> 'M/D/YY' (local, no UTC shift).
const fmtDate = (s?: string): string => {
  if (!s) return '-';
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return `${m}/${d}/${String(y).slice(2)}`;
};

const DetailModal: React.FC<Props> = ({ open, onClose, kind, title, rows, pros, range = null, onProClick, onAddServiceFailure }) => {
  // Only render the Pro # / Planned Date as actionable links when the user can
  // actually open a pro / add a service failure (both go through the Pro page).
  // Permissive when no provider is present (tests) — mirrors hasAccess defaults.
  const perms = useTryPermissions();
  const canViewPro = perms ? perms.hasAccess('/pro/:pronumber') : true;
  const canAddSf = canViewPro;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const hasDispatcher = useMemo(() => rows.some((r) => (r as LateDetail).dispatcher), [rows]);

  const baseColumns = useMemo<ColumnDef<Row>[]>(() => {
    const text = (key: string, header: string): ColumnDef<Row> => ({
      accessorKey: key,
      header,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => (getValue<string>() || '-'),
    });
    /** A column whose id collides with an orders-scope fieldname but whose data is
     *  something else — the SF's own comment, and the dispatcher the ROW is
     *  attributed to rather than the order's desk. Keeps its own header. */
    const ownText = (key: string, header: string): ColumnDef<Row> => ({
      ...text(key, header), meta: { registryTitle: false },
    });
    const date = (key: string, header: string): ColumnDef<Row> => ({
      accessorKey: key,
      header,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => fmtDate(getValue<string>()),
    });

    const proCol: ColumnDef<Row> = {
      accessorKey: 'pro',
      header: 'Pro #',
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const pro = row.original.pro;
        // No pro permission -> plain text, not a link.
        if (!canViewPro) return <span className="text-body">{pro}</span>;
        // Viewed/last-viewed highlight + click-to-mark is applied by TanTable's
        // trackViewedColumnId="pro" wrapper.
        return (
          <button
            type="button"
            onClick={() => onProClick(pro)}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
          >
            {pro}
          </button>
        );
      },
    };

    // SF + OTD share the full reschedule detail; Uncoded has no SF record so it
    // shows only the delivery context. SF Date sits before Delivery Appt.
    // Service Pass rows are the failing pros' controllable SFs — same columns.
    const withSf = kind === 'serviceFailures' || kind === 'otd' || kind === 'servicePass';
    const cols: ColumnDef<Row>[] = [
      proCol,
      text('customer', 'Customer'),
      text('delivery_location', 'Delivery Location'),
    ];
    if (withSf) cols.push(date('date', 'SF Date'));
    else if (kind === 'uncoded') {
      // Planned Date links to a New Service Failure for the pro, defaulted to
      // that planned date.
      cols.push({
        accessorKey: 'date',
        header: 'Planned Date',
        filterFn: 'arrIncludes',
        cell: ({ row }) => {
          const d = (row.original as UncodedDetail).date;
          // No SF permission (or no handler) -> plain text, not a link.
          if (!canAddSf || !onAddServiceFailure || !d) return fmtDate(d);
          return (
            <button
              type="button"
              onClick={() =>
                onAddServiceFailure(row.original.pro, d, row.original.billto)
              }
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
              title="Add service failure for this pro"
            >
              {fmtDate(d)}
            </button>
          );
        },
      });
    }
    else if (kind === 'apptsFinalized') cols.push(date('date', 'Finalized'));
    cols.push(date('delivery_appt', 'Delivery Appt'));
    if (withSf) {
      cols.push(
        text('reason', 'Reason'),
        text('type', 'SF Type'),
        text('coded_by', 'Coded By'),
        text('resp_driver', 'Resp Driver'),
        ownText('comments', 'Comments'),
      );
    }
    if (hasDispatcher) cols.push(ownText('dispatcher', 'Dispatcher'));
    return cols;
  }, [kind, hasDispatcher, onProClick, onAddServiceFailure, canViewPro, canAddSf]);

  // Added order columns. The selection is remembered per drill KIND, matching the
  // grid's own settingsKey — the fields worth seeing beside a service failure are
  // not the ones worth seeing beside an uncoded delivery. A CLOSED modal passes a
  // null key, which the hook reads as "no grid to remember for", so it neither
  // loads settings nor fires a lookup for a drill nobody has opened.
  const settingsKey = `dispatcher-metrics-detail-${kind}`;
  const fieldSelection = useAdditionalFieldSelection(open ? settingsKey : null);
  const { columns, availableFields } = useAdditionalFieldColumns<Row>(
    fieldSelection, { baseColumns, coveredFields: DETAIL_COVERED_FIELDS },
  );

  // These rows were derived in the browser, so the added columns' values have to be
  // fetched on their own and merged on.
  const { extras, loading: extrasLoading, error: extrasError } = useOrderFieldExtras({
    pros,
    additionalFields: fieldSelection.additionalFields,
    range,
    enabled: open,
  });
  const dataRows = useMemo(
    () => (Object.keys(extras).length === 0
      ? rows
      : rows.map((r) => {
        const ex = extras[String(r.pro)];
        return ex ? { ...r, ...ex } : r;
      })),
    [rows, extras],
  );

  const groupableColumns = useMemo(
    () => ['customer', 'delivery_location', 'reason', 'type', 'coded_by', 'resp_driver', 'dispatcher'],
    [],
  );

  // Read-only drill-down: Alt+C closes; the layer keeps Alt+S off the report grid.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-scrim/50 p-4" onClick={onClose}>
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative flex flex-col w-full max-w-6xl h-[80vh] max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Standard slate header: title + open-in-search + close. TanTable renders
            the filterable/searchable card header + toolbar below. */}
        <ModalHeader
          title={title || 'Detail'}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          className="flex-shrink-0"
          actions={
            pros.length > 0 ? (
              <a
                href={`/searchresults?q=${pros.join(',')}&multi=true`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-sky-300 hover:text-white hover:underline whitespace-nowrap"
              >
                Open {pros.length} pro{pros.length === 1 ? '' : 's'} in search
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </a>
            ) : undefined
          }
        />

        {extrasError && (
          <div className="flex-shrink-0 px-3 pt-2 text-xs text-red-600 dark:text-red-400">
            {extrasError} — the added columns are blank, not empty.
          </div>
        )}

        <div className="flex-1 min-h-0 p-3">
          <TanTable
            data={dataRows}
            columns={columns}
            availableFields={availableFields}
            additionalFields={fieldSelection.additionalFields}
            onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
            trackViewedColumnId="pro"
            loading={extrasLoading}
            searchPlaceholder="Search…"
            enableColumnFilters
            enableSearch
            enableGrouping
            groupableColumns={groupableColumns}
            enableShowHideCols
            enableExcelExport
            fillContainer
            disableVirtualization
            defaultPageSize={200}
            settingsKey={`dispatcher-metrics-detail-${kind}`}
            disableSettingsPersistence
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DetailModal;
